import { FC, useEffect, useRef, useState } from "react";
import { useField } from "formik";
import { FieldGroup, IFormProps } from ".";
import { RadioInput, EnumsType, EnumItemType } from "src/Components/Controls";
import DictionaryRegistryControlViewModel from "../Models/DictionaryRegistryControlViewModel";
import BaseViewModel from "../Models/BaseViewModel";
import API from "../API";
import { objToFormData } from "src/Utils";
import ViewModelDataCollector from "../ScriptEngine/ViewModelDataCollector";
import { SimpleSelect } from "@redoc/ui-kit";

interface IDictionaryRegistryControlProps extends IFormProps {
	model: DictionaryRegistryControlViewModel;
};

type DictOption = {
	id: string;
	title: string;
};

type SelectValue = unknown;

const isRecord = ( v: unknown ): v is Record<string, unknown> => typeof v === 'object' && v !== null;
const isEnumObject = ( v: EnumItemType ): v is { id: string | number; title?: string } =>
	typeof v === "object" && v !== null && "id" in v;
const getStringProp = ( v: Record<string, unknown>, key: string ): string | undefined => {
	const val = v[key];
	return typeof val === 'string' ? val : undefined;
};
const normalizeStr = ( s: string ) => s.replace(/["«»]/g, '').replace(/\s+/g, ' ').trim();
const appendMissingOptions = ( base: EnumsType, extra: Array<DictOption> ): EnumsType => {
	const resultOptions = [...base];
	extra.forEach( item => {
		const exists = resultOptions.some( option => isEnumObject(option) && String(option.id) === String(item.id) );
		if ( !exists ) {
			resultOptions.push(item);
		}
	});
	return resultOptions;
};
const toIdValue = ( v: SelectValue ): SelectValue => {
	if ( v === null || v === undefined ) return v;
	if ( isRecord(v) && !Array.isArray(v) ) {
		const id = getStringProp(v, 'id');
		if ( id ) return id;
	}
	if ( Array.isArray(v) ) {
		return v.map( (x: unknown) => toIdValue(x) );
	}
	return v;
};

type DictRequest = { dataUrl: string } & Record<string, unknown>;

/** Поле ДФ в виде справочника */
const DictionaryRegistryControl: FC<IDictionaryRegistryControlProps> = ( { model, readOnly, setFieldValue } ) => {

	/** Управление полем формика */
	const [ inputProps,, helperProps ] = useField( model.FullPath );
	/** Загруженные опции */
	const [ options, setOptions ] = useState<EnumsType>([]);
	/** Флаг загрузки */
	const [ isWaiting, setIsWaiting ] = useState<boolean>(false);
	/** Поисковая строка по списку опций */
	const [ searchStr, setSearchStr ] = useState<string>("");
	/** Флаг, чтобы не зациклиться, если уже отправляли запрос «найти текущее значение»(к примеру, когда нужно указать нашу организацию "Интегрикс тест", а ее нет в справочнике) */
	const currentSearchDone = useRef<boolean>(false);
	
	useEffect( () => {
		helperProps.setValue( model.Value, true );
	// eslint-disable-next-line
	}, [ model.Value ] );

	// parentId пересчитывается при каждом рендере — при смене родительского поля
	// форма перерисуется и эффект перезапустит загрузку с правильным фильтром
	const parentId = model.getParentIdFunc() ?? null;

	/** Поиск по списку */
	useEffect(() => onSearch(searchStr),[searchStr, parentId]);

	/**
	 * Функция для получения данных из справочника
	 * @param search Поисковая строка
	 * @param currentId Текущее ( выбранное ) значение — используется только для поиска
	 */
	const onSearch = ( search: string, currentId?: string ) => {
		const requestData = getDataRequest( search, 0, currentId );
		setIsWaiting(true);
		API.getDictionaryRegistryData( requestData.dataUrl, objToFormData(requestData) )
			.then( result => {
				if ( result.hasError ) {
					console.warn( result.errorMessage );
					// Не очищаем список при ошибке поиска текущего значения
					if ( !currentId ) setOptions( [] );
				}
				else if ( !!result.serializedValue ) {
					if ( currentId ) {
						// Запрос для поиска текущего значения: только кешируем найденный элемент
						const list = parseOptions(result.serializedValue);
						if ( list.length > 0 ) {
							model.metaData = [list[0]];
							// Добавляем найденный элемент в options, чтобы компонент смог показать его title
							setOptions( prev => {
								const resultOptions = appendMissingOptions( prev, list );
								model.options = resultOptions as DictOption[];
								return resultOptions;
							});
						} else {
							// Значение из контекста не найдено в справочнике — очищаем поле
							model.setValue(null);
							helperProps.setValue(null, true);
						}
					} else {
						const list = tryPrepareOptions(result.serializedValue);
						trySetCurrent(list);
					}
				}
			})
			.finally( () => setIsWaiting(false) );
	};

	/** Попытка найти выбранную опцию */
	const trySetCurrent = ( options: Array<DictOption> ) => {
		const selectedId = model.Value;
		if ( !selectedId ) return;
		// Ежели в metaData что-то есть, значит и не паримся, текущее значение закэшировано там...
		if ( !!model.metaData.length ) return;
		const item = options.find( (x) => x.id === selectedId );
		if ( !item ) {
			// Если уже делали запрос «найти текущее значение» — не повторяем, иначе будет бесконечный цикл
			if ( currentSearchDone.current ) return;
			currentSearchDone.current = true;
			const id = (typeof selectedId === 'string') ? normalizeStr(selectedId) : String(selectedId);
			// Не нашли( отправляем запрос...
			onSearch('', id);
		}
		else {
			// Раз нашли значит она будет в списке...
			// поместим ее еще в metaData, так сделано в старом интерфейсе и будет служить нам временной переменной
			model.metaData = [item];
		}
	};

	/** Преобразование ответа в массив опций */
	const parseOptions = ( serializedValue: string ): Array<DictOption> => {
		try {
			const currentItems = JSON.parse(serializedValue) as Array<{ id: string, text: string }>;
			return currentItems
				.map( (i) => ({
					id: typeof i.id === 'string' ? i.id : String(i.id ?? ''),
					title: typeof i.text === 'string' ? i.text : String(i.text ?? '')
				}))
				.filter( x => x.id.length > 0 );
		}
		catch ( error: unknown ) {
			console.warn( error );
			return [];
		}
	};

	/** Преобразование ответа в массив опций и обновление состояния */
	const tryPrepareOptions = ( serializedValue: string ) => {
		const _data = parseOptions(serializedValue);
		// Добавляем в список опций закэшированное значение из metaData, чтобы при медленном ответе основного списка оно не затиралось, 
		// а также чтобы при переходах по страницам выбранное значение оставалось в options
		const resultOptions = model.metaData.length > 0 ? appendMissingOptions( _data, model.metaData as Array<DictOption> ) : _data;
		setOptions( resultOptions );
		model.options = resultOptions as DictOption[];
		return resultOptions as Array<DictOption>;
	};

	/**
	 * Генерация объекта запроса справочника согласно настроек модели
	 * @param term Поисковая строка
	 * @param page страница ( не используется )
	 * @param current текущее значение
	 * @returns объект запроса справочника
	 */
	const getDataRequest = ( term?: string, page: number = 0, current?: string ) => {
		// DisplayFieldScript в дескрипторе хранится как ScriptDescriptionType-объект, но сервер ожидает строку
		const rawScript = model.DisplayFieldScript as { Content?: string } | string | null | undefined;
		const displayFieldScriptStr: string | undefined =
			typeof rawScript === 'string' ? rawScript
			: (rawScript?.Content ?? undefined);

		const preModel: DictRequest = {
			dataUrl: model.dataUrl,
			term: term,
			needLoad: 200,
			loadedEntities: ((page || 1) - 1),

			fileId: BaseViewModel.fileId,
			componentId: BaseViewModel.componentId,

			dictionaryId: model.dataDict,
			currentPath: model.Path,
			displayFieldScript: displayFieldScriptStr
		};

		if ( !!current ) {
			preModel.current = current;
		}

		if ( model.DictionarySource?.startsWith('Value') ) {
			preModel.parentId = model.dataParent;
			preModel.collectFormData = JSON.stringify(ViewModelDataCollector.collectFormDataKoVm( model ));
			preModel.typename = BaseViewModel.typename;
			preModel.projectNameWithSuffix = BaseViewModel.projectNameWithSuffix;
			preModel.neededPath = model.DictionarySource;
		}
		else {
			preModel.parentId = model.getParentIdFunc();
			if ( model.ExternalRequest ) {
				preModel.collectFormData = JSON.stringify(ViewModelDataCollector.collectFormDataKoVm( model ));
				preModel.typename = BaseViewModel.typename;
				preModel.projectNameWithSuffix = BaseViewModel.projectNameWithSuffix;
			}
		}

		return preModel;
	};

	/** При закрытии списка, вернем закэшированное текущее значение в опции, что бы не пялиться на ИДшник... */
	const onCloseSelectBox = () => {
		if ( model.metaData.length > 0 ) {
			setOptions( prev => {
				const resultOptions = appendMissingOptions( prev, model.metaData as Array<DictOption> );
				model.options = resultOptions as DictOption[];
				return resultOptions;
			} );
		}
	};

	/** Функция устанавливает текущее значение */
	const setModelValue = ( v: SelectValue ) => {
		const id = toIdValue(v);
		setFieldValue( model.FullPath, id as never );
		model.setValue( id as never );
		const item = options.find( (x:any) => x.id === id );
		if ( !item ) throw new Error("Не удалось получить устанавливаемое опцию(");
		model.metaData = [item];
	};

	/** Отрисовка инпута */
	const renderInput = () => {
		if ( model.ControlKey === "RadioGroup" ) {
			return (
				<RadioInput 
					name={ model.FullPath }
					disabled={readOnly || !model.ControlEnabling}
					items={options}
					value={ inputProps.value }
					isCol={false}
					noUnderLine
					onChangeValue={setModelValue}
				/>
			);
		}
		else return (
			<div style={{ width: '100%' }}>
				<SimpleSelect
					name={ model.FullPath }
					setValue={setModelValue}
					onClose={onCloseSelectBox}
					placeholder={model.nullTitle}
					className={`${model.IsValid ? '' : 'st.error'}`}
					disabled={readOnly || !model.ControlEnabling}
					options={options}
					isOwnerList={true}
					multiple={model.multiflag}
					value={ inputProps.value }
					onSearch={setSearchStr}
					loadOnOpen
					searchEnable
					isWait={isWaiting}
					isPortal={true}
				/>
			</div>
		);
	};

	return (
		<FieldGroup
			hidden={!model.ControlVisibility}
			name={model.FullPath}
			isInvalid={!model.IsValid} 
			errorMsg={model.errorMessage}
			title={model.Caption} 
			width={model.Width ?? 12} 
			isRequired={model.IsMandatory}
			readOnly={readOnly}
		>
			{ renderInput() }
		</FieldGroup>
	);
};

export default DictionaryRegistryControl;
